/**
 * Utility functions for API operations
 */
import { User } from '../types';

/**
 * Map a user from the backend format to the frontend format
 * @param backendUser The user data from the backend API
 * @returns A user object in the frontend format
 */
export const mapUserFromBackend = (backendUser: any): User | null => {
  if (!backendUser) return null;
  
  // Map the role from a number to a string
  let role = 'unknown';
  if (backendUser.role !== undefined) {
    // Backend uses enum (0 = SuperAdmin, 1 = Admin, 2 = Manager, 3 = Guard)
    switch (backendUser.role) {
      case 0:
        role = 'SuperAdmin';
        break;
      case 1:
        role = 'Admin';
        break;
      case 2:
        role = 'Manager';
        break;
      case 3:
        role = 'Guard';
        break;
      default:
        // If it's already a string, use it directly
        if (typeof backendUser.role === 'string') {
          role = backendUser.role;
        }
    }
  }
  
  // Create a base user object with the required properties
  const user: User = {
    id: backendUser.id,
    account: backendUser.account || backendUser.email,
    name: backendUser.name || 
          (backendUser.firstName && backendUser.lastName ? 
           `${backendUser.firstName} ${backendUser.lastName}`.trim() : 
           backendUser.firstName || backendUser.lastName || ''),
    role: role,
    companyId: backendUser.companyId,
    parentUserId: backendUser.parentUserId,
    createdAt: backendUser.createdAt,
    lastLoginAt: backendUser.lastLoginAt,
    certificateNumber: backendUser.certificateNumber || null,
    certificateExpiration: backendUser.certificateExpiration || null,
  };
  
  return user;
};

/**
 * Normalize a role value to a canonical PascalCase string.
 * Handles numeric enums (0-3), numeric strings ("3"), case variations ("guard", "GUARD"),
 * undefined, and null. Returns "unknown" for unrecognized values.
 */
export const normalizeRole = (role: string | number | undefined | null): string => {
  if (role === undefined || role === null) return 'unknown';

  if (typeof role === 'number') {
    switch (role) {
      case 0: return 'SuperAdmin';
      case 1: return 'Admin';
      case 2: return 'Manager';
      case 3: return 'Guard';
      default: return 'unknown';
    }
  }

  // Handle numeric strings (e.g. "3" from JSON round-tripping)
  const parsed = parseInt(role, 10);
  if (!isNaN(parsed) && String(parsed) === role) {
    return normalizeRole(parsed);
  }

  // Case-insensitive string matching
  switch (role.toLowerCase()) {
    case 'superadmin': return 'SuperAdmin';
    case 'admin': return 'Admin';
    case 'manager': return 'Manager';
    case 'guard': return 'Guard';
    default: return 'unknown';
  }
};

export const isGuard = (role: string | number | undefined | null): boolean =>
  normalizeRole(role) === 'Guard';

export const isAdmin = (role: string | number | undefined | null): boolean =>
  normalizeRole(role) === 'Admin';

export const isManager = (role: string | number | undefined | null): boolean =>
  normalizeRole(role) === 'Manager';

export const isSuperAdmin = (role: string | number | undefined | null): boolean =>
  normalizeRole(role) === 'SuperAdmin';

export const isAdminOrManager = (role: string | number | undefined | null): boolean => {
  const normalized = normalizeRole(role);
  return normalized === 'Admin' || normalized === 'Manager';
};

/**
 * Handle API errors in a consistent way
 * @param error The error object from the API call
 * @returns A standardized error object
 */
export const handleApiError = (error: any): Error => {
  console.error('API Error:', error);
  
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('Response data:', error.response.data);
    console.error('Response status:', error.response.status);
    
    // Return a more specific error if available
    if (error.response.data && error.response.data.message) {
      return new Error(error.response.data.message);
    }
    
    return new Error(`API Error: ${error.response.status}`);
  } else if (error.request) {
    // The request was made but no response was received
    console.error('Request error:', error.request);
    return new Error('No response received from server');
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('Error message:', error.message);
    return new Error(error.message || 'Unknown error occurred');
  }
};
